import React, { useEffect, useState } from "react";
import markSrc from "@/assets/codex-mark.png";
import { getUniverse, heroUrl } from "@/lib/api";

const SESSION_KEY = "codex-booted";
const CACHE_KEY = "codex-hero-mosaic"; // persists the hero-image list across launches
const MIN_MS = 1300;   // minimum on-screen time so it never "flashes"
const REVEAL_MS = 760; // keyhole fly-through duration
const TILE_COUNT = 24;

// Known-good hero images so the very first visit (before any cache) still shows
// a populated mosaic instead of bare navy.
const FALLBACK = [
  "/api/files/codex/hero/nephilim.png",
  "/api/files/codex/hero/divine-council.png",
  "/api/files/codex/hero/deuteronomy-32.png",
  "/api/files/codex/hero/psalm-82.png",
];

// Tile the archive hero artwork out to fill the whole grid. The list is shuffled
// so bright pieces scatter evenly across the grid (they otherwise cluster and
// leave dark voids), and it varies subtly on each launch.
const shuffle = (a) => {
  const arr = [...a];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};
const buildTiles = (hero, n = TILE_COUNT) => {
  const H = shuffle((Array.isArray(hero) ? hero : []).filter(Boolean));
  if (!H.length) return [];
  const out = [];
  for (let i = 0; i < n; i++) out.push(H[i % H.length]);
  return out;
};

const readCache = () => {
  try {
    const c = JSON.parse(localStorage.getItem(CACHE_KEY) || "[]");
    if (Array.isArray(c) && c.length) return c;
  } catch (e) {}
  return null;
};

/**
 * CODEX boot splash — "Unlocking the Archive".
 * Shows once per app launch (cold boot / PWA open). Navy field + the CODEX
 * emblem (whose pupil is a keyhole), a golden key that slides up and turns to
 * "unlock", over a softly drifting, blurred mosaic of real archive artwork.
 * Then it flies through the keyhole (scale + fade) to reveal the app.
 */
export function SplashScreen() {
  const [show, setShow] = useState(() => {
    try { return !sessionStorage.getItem(SESSION_KEY); } catch (e) { return true; }
  });
  const [phase, setPhase] = useState("enter"); // "enter" -> "reveal"
  // Seed from cache (instant on repeat launches), else known-good fallback.
  const [tiles, setTiles] = useState(() => buildTiles(readCache() || FALLBACK));

  useEffect(() => {
    if (!show) return;
    try { sessionStorage.setItem(SESSION_KEY, "1"); } catch (e) {}
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    // Refresh the hero-image list in the background and cache it for next time.
    let alive = true;
    getUniverse({ limit: 90 })
      .then((d) => {
        if (!alive) return;
        const imgs = Array.from(
          new Set((d.nodes || []).map((n) => n && n.hero_image).filter(Boolean).map((p) => heroUrl(p)))
        ).slice(0, TILE_COUNT);
        if (imgs.length) {
          setTiles(buildTiles(imgs));
          try { localStorage.setItem(CACHE_KEY, JSON.stringify(imgs)); } catch (e) {}
        }
      })
      .catch(() => {});

    const t1 = setTimeout(() => setPhase("reveal"), MIN_MS);
    const t2 = setTimeout(() => {
      setShow(false);
      document.body.style.overflow = prevOverflow;
    }, MIN_MS + REVEAL_MS);

    return () => {
      alive = false;
      clearTimeout(t1);
      clearTimeout(t2);
      document.body.style.overflow = prevOverflow;
    };
  }, [show]);

  if (!show) return null;

  return (
    <div
      data-testid="boot-splash"
      className={`codex-splash${phase === "reveal" ? " codex-splash-reveal" : ""}`}
      aria-hidden="true"
    >
      {/* Blurred, drifting mosaic of archive artwork */}
      <div className="codex-splash-mosaic" data-testid="boot-splash-mosaic">
        {tiles.map((src, i) => (
          <div key={i} className="codex-splash-tile" style={{ backgroundImage: `url("${src}")` }} />
        ))}
      </div>
      <div className="codex-splash-veil" />

      {/* Center lock */}
      <div className="codex-splash-center">
        <div className="codex-splash-mark-wrap">
          <img src={markSrc} alt="" className="codex-splash-mark" draggable="false" />
          <span className="codex-splash-unlock" />
          <svg
            className="codex-splash-key"
            width="26"
            height="66"
            viewBox="0 0 28 72"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs>
              <linearGradient id="codexKeyGold" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="#F6E4A8" />
                <stop offset="0.5" stopColor="#D8B66A" />
                <stop offset="1" stopColor="#9C7529" />
              </linearGradient>
            </defs>
            <rect x="11.5" y="5" width="5" height="46" rx="1.6" fill="url(#codexKeyGold)" />
            <rect x="16.5" y="12" width="7" height="3.4" rx="1" fill="url(#codexKeyGold)" />
            <rect x="16.5" y="20" width="5" height="3.4" rx="1" fill="url(#codexKeyGold)" />
            <circle cx="14" cy="60" r="9.5" fill="none" stroke="url(#codexKeyGold)" strokeWidth="4.5" />
          </svg>
        </div>

        <div className="codex-splash-word">CODEX</div>
        <div className="codex-splash-tag">Unlocking the archive&hellip;</div>
        <div className="codex-splash-bar"><span /></div>
      </div>
    </div>
  );
}
